import FileSaver from 'file-saver';
import * as Tesseract from 'tesseract.js';
import {helpers} from '../../state/services/helpers';

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// U+FEFF encodes to the EF BB BF byte-order mark once the Blob is written as UTF-8.
const UTF8_BOM = '\uFEFF';

/**
 * Collection of file utility helpers for encoding, decoding, OCR, and file downloads.
 * @namespace fileHelpers
 * @example
 * import { fileHelpers } from './file.helpers';
 */
export const fileHelpers = {
  /**
   * Converts a base64-encoded string to a Blob with the given content type.
   * @param {string} b64Data - Base64-encoded file data.
   * @param {string} [contentType=''] - MIME type of the resulting Blob.
   * @param {number} [sliceSize=512] - Byte size of each processing slice.
   * @returns {Blob} Blob object containing the decoded data.
   * @example
   * fileHelpers.b64toBlob(base64String, 'application/pdf');
   */
  b64toBlob: (b64Data, contentType, sliceSize) => {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    let byteCharacters = atob(b64Data);
    let byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      let slice = byteCharacters.slice(offset, offset + sliceSize);

      let byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      let byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, {type: contentType});
  },
  /**
   * Returns true when the given filename ends with one of the allowed extensions.
   * @param {string} filename - File name or path to test.
   * @param {string[]} extensions - Array of allowed extensions including the dot (e.g. ['.pdf', '.png']).
   * @returns {boolean} True if the filename matches one of the extensions.
   * @example
   * fileHelpers.hasExtensions('document.pdf', ['.pdf', '.docx']); // true
   */
  hasExtensions: (filename, extensions) => {
    return (new RegExp('(' + extensions.join('|').replace(/\./g, '\\.') + ')$')).test(filename);
  },
  /**
   * Triggers a CSV file download for the given data with the specified filename.
   *
   * The leading BOM is not decoration. Excel ignores the blob's `charset=utf-8` and decodes CSV
   * using the system locale instead, so on an Italian machine a correct UTF-8 file renders
   * `Società` as `SocietÃ ` — and a file the client has to hand-repair is a failed export. The BOM
   * is the only thing Excel actually honours here. Other parsers strip it, so this is safe for
   * every caller.
   *
   * Idempotent, and a no-op for anything that is not a string, so a caller that already prefixed
   * one — or that passes a Blob — is left alone.
   *
   * @param {string} data - CSV content string.
   * @param {string} filename - Base filename without extension; '.csv' is appended automatically.
   * @returns {void}
   * @example
   * fileHelpers.handleCSVExport(csvString, 'transactions-2024-03');
   */
  handleCSVExport: (data, filename) => helpers.saveFile(
    typeof data === 'string' && !data.startsWith(UTF8_BOM) ? `${UTF8_BOM}${data}` : data,
    `${filename}.csv`,
    'text/csv',
  ),
  /**
   * Removes the data URI prefix from a base64 image string, returning only the raw base64 data.
   * @param {string|null} img - Data URI string (e.g. 'data:image/png;base64,...') or null.
   * @returns {string|null} Base64 data portion after the comma, or null if input is null.
   * @example
   * fileHelpers.cutFileData('data:image/png;base64,iVBORw0KGgo='); // 'iVBORw0KGgo='
   */
  cutFileData: (img) => {
    let cutFile = null;
    if (img !== null) {
      let index = img.lastIndexOf(',');
      cutFile = index > 0 ? img.substring(index + 1) : img;
    }
    return cutFile;
  },
  /**
   * Reads a File object and resolves with its base64-encoded data URI.
   * @param {File} file - File object to read.
   * @returns {Promise<string>} Resolved with the base64 data URI string.
   * @throws {ProgressEvent} Rejects with the FileReader error event on read failure.
   * @example
   * const dataUrl = await fileHelpers.getBase64(fileInput.files[0]);
   */
  getBase64: (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  }),
  /**
   * Reads a File and shapes it into the attachment draft the agent chat composer stages
   * before sending: antd's Attachments preview fields (name/size/type/status) plus the
   * base64 payload the websocket protocol needs once the draft is actually sent.
   * @param {File} file - File object to encode.
   * @param {string} uid - Unique id for the draft (antd Upload's own uid, or a generated one).
   * @returns {Promise<{uid: string, name: string, filename: string, size: number, type: string, status: string, base64: string}>}
   * @example
   * const draft = await fileHelpers.buildAttachmentDraft(file, file.uid);
   */
  buildAttachmentDraft: async (file, uid) => ({
    uid,
    name: file.name,
    filename: file.name,
    size: file.size,
    type: file.type,
    status: 'done',
    base64: await fileHelpers.getBase64(file),
  }),
  /**
   * Converts a comma-separated accept string of file extensions into a human-readable uppercase list.
   * @param {string|null} formatsList - Comma-separated accept attribute value (e.g. '.jpg,.png,.pdf').
   * @returns {string|null} Comma-separated uppercase extension list (e.g. 'JPG, PNG, PDF'), or null if input is falsy.
   * @example
   * fileHelpers.getSupportedFormatsList('.jpg,.png,.pdf'); // 'JPG, PNG, PDF'
   */
  getSupportedFormatsList: (formatsList) => {
    let formats = null;
    let pattern = /\.([0-9a-z]+)(?:[?#]|$)/i;
    if (formatsList) {
      formats = formatsList.split(',')
        .map(a => a.match(pattern))
        .map(f => f ? f.length === 2 ? f[1] : null : null)
        .filter(f => f)
        .map(f => f.toUpperCase())
        .join(', ');
    }
    return formats;
  },
  /**
   * Uses Tesseract.js to perform OCR on an image, calling the appropriate callback with the result.
   * @param {string} image - Base64 data URI or URL of the image to recognise.
   * @param {Function} [successCallback] - Called with the Tesseract result object on success.
   * @param {Function} [errorCallback] - Called with the error on failure.
   * @returns {void}
   * @example
   * fileHelpers.recognizeImageToText(imageDataUrl, (result) => console.log(result.data.text), console.error);
   */
  recognizeImageToText: (image, successCallback, errorCallback) => {
    Tesseract.recognize(image, "eng")
      .then((result) => successCallback && successCallback(result))
      .catch((err) => errorCallback && errorCallback(err));
  },
  /**
   * Extracts the filename from a URL, stripping query strings and hash fragments.
   * @param {string} url - URL string to parse.
   * @returns {string|null} Decoded filename or null if extraction fails.
   * @example
   * fileHelpers.getFileNameFromUrl('https://example.com/files/report.pdf?v=2'); // 'report.pdf'
   */
  getFileNameFromUrl: (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
      // Use the URL API when possible
      const parsed = new URL(url, window?.location?.href);
      const pathname = parsed.pathname || ''; // already excludes query/hash
      const parts = pathname.split('/').filter(Boolean);
      const last = parts.length ? parts[parts.length - 1] : null;
      return last ? decodeURIComponent(last) : null;
    } catch (e) {
      // Fallback regex if URL parsing fails
      const match = url.match(/\/([^/?#]+)(?:[#?]|$)/);
      return match ? decodeURIComponent(match[1]) : null;
    }
  },
  /**
   * Decodes a base64 file response object and triggers a browser download using FileSaver.
   * @param {{file: string, 'content-type': string, filename: string}} data - File response object with base64 content.
   * @returns {void}
   * @example
   * fileHelpers.saveFile({ file: base64String, 'content-type': 'application/pdf', filename: 'invoice.pdf' });
   */
  saveFile: (data) => {
    const blob = fileHelpers.b64toBlob(data.file, data['content-type']);
    FileSaver.saveAs(blob, data.filename);
  },
  /**
   * Triggers a download for the envelope the export endpoints return for spreadsheet formats.
   *
   * Both backends answer with the same shape — `{file, file_name, extension}` — even though they
   * disagree on the query parameter that asks for it (`export_format` on app-accounting,
   * `format` on app-backend). The name and extension come from the response rather than the
   * caller, because the backend already knows both and reports `.xlsx`.
   *
   * @param {{file: string, file_name?: string, extension?: string}} response - Export response.
   * @param {string} fallbackName - Used only if the response omits `file_name`.
   * @param {string} [contentType] - MIME type; defaults to xlsx.
   * @returns {void}
   * @example
   * fileHelpers.saveExportedFile(response, 'journal-entries');
   */
  saveExportedFile: (response, fallbackName, contentType = XLSX_CONTENT_TYPE) => {
    fileHelpers.saveFile({
      file: response?.file,
      'content-type': contentType,
      filename: `${response?.file_name || fallbackName}${response?.extension || '.xlsx'}`,
    });
  },
  /**
   * Triggers a browser download for a raw binary response (e.g. a Blob or ArrayBuffer from a `responseType: 'blob'` request).
   * @param {Blob|ArrayBuffer} data - Raw binary file content.
   * @param {string} filename - Filename including extension (e.g. 'payout-123.pdf').
   * @param {string} [contentType='application/pdf'] - MIME type for the resulting Blob.
   * @returns {void}
   * @example
   * fileHelpers.saveBlobFile(response, 'payout-123.pdf');
   */
  saveBlobFile: (data, filename, contentType = 'application/pdf') => {
    FileSaver.saveAs(new Blob([data], {type: contentType}), filename);
  },
}
